/* From the archive: register interest in a sold-out product or size.
   Each request records a Klaviyo back in stock subscription for one variant, so
   Klaviyo counts interest per product and size and can email if a piece returns.
   It never subscribes anyone to a marketing list and never touches add to cart.
   (Files and classes keep their original "vault" names.) */
(() => {
  'use strict';

  const ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  // GA revision shown in Klaviyo's reference for Create Client Back In Stock Subscription.
  const REVISION = '2026-10-15';
  const TIMEOUT_MS = 12000;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
  const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
  const PILL_GAP = 6;
  const PILL_MIN = 40;

  function variantRef(variantId) {
    return `$shopify:::$default:::${variantId}`;
  }

  function requestUrl(companyId) {
    return `${ENDPOINT}?company_id=${encodeURIComponent(companyId)}`;
  }

  function buildPayload(email, variantId) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email } } }
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: variantRef(variantId) } }
        }
      }
    };
  }

  function isValidEmail(value) {
    return EMAIL_PATTERN.test(String(value || '').trim());
  }

  // Klaviyo answers 202 on success. A 400 about the email is the shopper's to fix;
  // anything else (unknown variant, rate limit, outage) gets the plain retry message.
  function classifyFailure(status, body) {
    const errors = body && Array.isArray(body.errors) ? body.errors : [];
    const aboutEmail = errors.some(error => {
      const pointer = String((error && error.source && error.source.pointer) || '');
      const text = `${(error && error.code) || ''} ${(error && error.detail) || ''}`.toLowerCase();
      return /email/i.test(pointer) || /email/.test(text);
    });
    return status === 400 && aboutEmail ? 'email' : 'general';
  }

  // variants: the sizes on show (one colour at a time); selected: the pressed size, if any.
  function vaultState(variants, selected) {
    const list = Array.isArray(variants) ? variants.filter(Boolean) : [];
    const soldOut = list.filter(variant => !variant.available);
    if (!soldOut.length) return { mode: '', soldOut, preferred: null };
    const selectedOut = selected ? soldOut.find(variant => String(variant.id) === String(selected.id)) : null;
    const allOut = soldOut.length === list.length;
    return {
      mode: allOut || selectedOut ? 'primary' : 'secondary',
      soldOut,
      preferred: selectedOut || soldOut[0]
    };
  }

  // Same column rule as the product page size row: as many as fit at PILL_MIN wide.
  function pillColumns(total, width) {
    let columns = Math.max(1, total);
    while (columns > 1 && (width - PILL_GAP * (columns - 1)) / columns < PILL_MIN) columns -= 1;
    return columns;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { REVISION, variantRef, requestUrl, buildPayload, isValidEmail, classifyFailure, vaultState, pillColumns };
  }
  if (typeof customElements === 'undefined' || customElements.get('rd-vault-interest')) return;

  customElements.define('rd-vault-interest', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const on = (target, type, handler) => { if (target) target.addEventListener(type, handler, { signal }); };
      const id = this.dataset.sectionId;

      this.companyId = (this.dataset.companyId || '').trim();
      this.variants = this.readVariants();
      this.byId = new Map(this.variants.map(variant => [String(variant.id), variant]));
      this.dialog = this.querySelector('dialog');
      this.pdp = document.getElementById(`rd-pdp-${id}`);
      this.buySlot = document.getElementById(`rd-buy-slot-${id}`);
      this.sizesWrap = document.getElementById(`rd-sizes-${id}`);
      this.variantInput = document.querySelector(`#rd-product-form-${id} .product-variant-id`);
      this.triggers = this.pdp ? [...this.pdp.querySelectorAll('[data-rd-vault-open]')] : [];
      if (!this.companyId || !this.dialog || !this.variants.length || !this.triggers.length) return;

      this.form = this.querySelector('[data-rd-vault-form]');
      this.formView = this.querySelector('[data-rd-vault-form-view]');
      this.success = this.querySelector('[data-rd-vault-success]');
      this.successMeta = this.querySelector('[data-rd-vault-success-meta]');
      this.email = this.querySelector('[data-rd-vault-email]');
      this.errorEl = this.querySelector('[data-rd-vault-error]');
      this.submitBtn = this.querySelector('[data-rd-vault-submit]');
      this.productLine = this.querySelector('[data-rd-vault-product]');
      this.sizeField = this.querySelector('[data-rd-vault-size-field]');
      this.sizeName = this.querySelector('[data-rd-vault-size-name]');
      this.pills = this.querySelector('[data-rd-vault-sizes]');
      this.options = [];
      this.chosen = null;
      this.contextCount = 0;

      this.triggers.forEach(trigger => on(trigger, 'click', event => { event.preventDefault(); this.open(trigger); }));
      this.querySelectorAll('[data-rd-vault-close]').forEach(button => on(button, 'click', () => this.close()));
      on(this.dialog, 'cancel', event => { event.preventDefault(); this.close(); });
      on(this.dialog, 'close', () => this.afterClose());
      on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
      });
      on(this.dialog, 'keydown', event => this.trapTab(event));
      on(this.form, 'submit', event => this.submit(event));
      on(this.email, 'input', () => { if (this.email.getAttribute('aria-invalid') === 'true') this.clearError(); });
      on(this.pills, 'click', event => {
        const pill = event.target.closest('.rd-pdp__size-btn');
        if (pill) this.choose(this.options[Number(pill.dataset.index)]);
      });
      on(this.pills, 'keydown', event => this.pillKey(event));
      on(window, 'resize', () => { if (this.isOpen) this.layoutPills(); });
      on(window, 'pagehide', () => this.close());

      if (this.sizesWrap && typeof MutationObserver === 'function') {
        // Mutation records arrive once per task, after the picker finishes re-rendering.
        this.observer = new MutationObserver(() => this.sync());
        this.observer.observe(this.sizesWrap, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-pressed', 'data-available', 'disabled'] });
      }
      this.sync();
    }

    disconnectedCallback() {
      this.close();
      if (this.observer) this.observer.disconnect();
      if (this.controller) this.controller.abort();
      this.observer = null;
      this.controller = null;
    }

    readVariants() {
      try {
        const data = JSON.parse(this.querySelector('[data-rd-vault-variants]').textContent);
        return Array.isArray(data) ? data.filter(variant => variant && variant.id) : [];
      } catch (error) {
        return [];
      }
    }

    // The size buttons are what the shopper sees, so their state wins over page-load data.
    context() {
      if (!this.sizesWrap) {
        const current = this.variantInput ? this.byId.get(String(this.variantInput.value)) : null;
        return { variants: this.variants, selected: current || (this.variants.length === 1 ? this.variants[0] : null) };
      }
      const variants = [];
      let selected = null;
      this.sizesWrap.querySelectorAll('[data-variant-id]').forEach(button => {
        const known = this.byId.get(String(button.dataset.variantId));
        if (!known) return;
        const variant = Object.assign({}, known, {
          available: button.dataset.available ? button.dataset.available === 'true' : known.available
        });
        variants.push(variant);
        if (button.getAttribute('aria-pressed') === 'true') selected = variant;
      });
      return { variants, selected };
    }

    sync() {
      const { variants, selected } = this.context();
      const { mode } = vaultState(variants, selected);
      this.triggers.forEach(trigger => { trigger.hidden = trigger.dataset.rdVaultOpen !== mode; });
      if (!this.buySlot) return;
      if (mode) this.buySlot.setAttribute('data-rd-vault-mode', mode);
      else this.buySlot.removeAttribute('data-rd-vault-mode');
    }

    open(trigger) {
      if (this.isOpen) return;
      const { variants, selected } = this.context();
      const state = vaultState(variants, selected);
      if (!state.soldOut.length) { this.sync(); return; }
      this.options = state.soldOut;
      this.contextCount = variants.length;
      this.renderPills();
      this.choose(state.preferred);
      this.showView('form');
      this.clearError();
      this.setSending(false);
      this.returnFocus = trigger;
      this.isOpen = true;
      this.oldOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      try {
        this.dialog.showModal();
      } catch (error) {
        this.dialog.setAttribute('open', '');
      }
      this.layoutPills();
      this.triggers.forEach(button => button.setAttribute('aria-expanded', 'true'));
      const closeButton = this.querySelector('[data-rd-vault-close]');
      if (closeButton) closeButton.focus({ preventScroll: true });
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
      this.afterClose();
    }

    afterClose() {
      if (!this.isOpen) return;
      this.isOpen = false;
      document.body.style.overflow = this.oldOverflow || '';
      this.triggers.forEach(button => button.setAttribute('aria-expanded', 'false'));
      if (!this.isConnected) return;
      this.sync();
      const previous = this.returnFocus;
      this.returnFocus = null;
      const fallback = this.triggers.find(button => !button.hidden) || document.getElementById(`rd-atc-${this.dataset.sectionId}`);
      const target = previous && previous.isConnected && !previous.hidden ? previous : fallback;
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
    }

    trapTab(event) {
      if (event.key !== 'Tab' || !this.dialog.open) return;
      const focusable = [...this.dialog.querySelectorAll(FOCUSABLE)].filter(node => node.tabIndex >= 0 && !node.closest('[hidden]') && node.getClientRects().length > 0);
      if (!focusable.length) { event.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      const index = focusable.indexOf(current);
      if (!this.dialog.contains(current)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey) {
        const beforeFirst = index === 0 || (index < 0 && current !== this.dialog && !(first.compareDocumentPosition(current) & Node.DOCUMENT_POSITION_FOLLOWING));
        if (beforeFirst) { event.preventDefault(); last.focus(); }
      } else {
        const afterLast = index === focusable.length - 1 || (index < 0 && current !== this.dialog && !(current.compareDocumentPosition(last) & Node.DOCUMENT_POSITION_FOLLOWING));
        if (afterLast) { event.preventDefault(); first.focus(); }
      }
    }

    sizeLabel(variant) {
      return variant && variant.size != null ? String(variant.size) : '';
    }

    // Sold-out sizes only, drawn with the product page's own size pill classes.
    renderPills() {
      if (!this.pills) return;
      this.pills.replaceChildren(...this.options.map((variant, index) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'rd-pdp__size-btn';
        pill.setAttribute('role', 'radio');
        pill.setAttribute('aria-checked', 'false');
        pill.tabIndex = -1;
        pill.dataset.index = String(index);
        pill.dataset.variantId = String(variant.id);
        pill.textContent = this.sizeLabel(variant);
        return pill;
      }));
    }

    layoutPills() {
      if (!this.pills || !this.pills.clientWidth) return;
      this.pills.style.gridTemplateColumns = `repeat(${pillColumns(this.contextCount || this.options.length, this.pills.clientWidth)}, 1fr)`;
    }

    choose(variant, focus) {
      if (!variant) return;
      this.chosen = variant;
      if (this.pills) {
        [...this.pills.children].forEach(pill => {
          const selected = pill.dataset.variantId === String(variant.id);
          pill.classList.toggle('rd-pdp__size-btn--selected', selected);
          pill.setAttribute('aria-checked', String(selected));
          pill.tabIndex = selected ? 0 : -1;
          if (selected && focus) pill.focus();
        });
      }
      if (this.productLine) {
        const title = this.productLine.dataset.title || '';
        this.productLine.textContent = variant.colour ? `${title} · ${variant.colour}` : title;
      }
    }

    // Radio pills: arrows move and select, wrapping; Home and End jump to the ends.
    pillKey(event) {
      const count = this.options.length;
      if (!count) return;
      const current = Math.max(0, this.options.findIndex(variant => this.chosen && String(variant.id) === String(this.chosen.id)));
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % count;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + count) % count;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = count - 1;
      if (next === null) return;
      event.preventDefault();
      this.choose(this.options[next], true);
    }

    showView(view) {
      if (this.formView) this.formView.hidden = view !== 'form';
      if (this.success) this.success.hidden = view !== 'success';
    }

    showError(message, emailProblem) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
      if (emailProblem) this.email.setAttribute('aria-invalid', 'true');
      else this.email.removeAttribute('aria-invalid');
    }

    clearError() {
      this.showError('', false);
    }

    setSending(sending) {
      this.sending = sending;
      if (!this.submitBtn) return;
      this.submitBtn.setAttribute('aria-disabled', String(sending));
      this.submitBtn.toggleAttribute('aria-busy', sending);
      this.form.classList.toggle('is-sending', sending);
    }

    showSuccess(variant) {
      if (this.successMeta) {
        const size = this.sizeField ? this.sizeLabel(variant) : '';
        const name = this.sizeName ? this.sizeName.textContent.trim() : '';
        this.successMeta.textContent = size ? `${name} ${size}`.trim() : '';
        this.successMeta.hidden = !size;
      }
      this.showView('success');
      if (this.success) this.success.focus({ preventScroll: true });
    }

    async submit(event) {
      event.preventDefault();
      if (this.sending) return;
      const email = this.email.value.trim();
      if (!isValidEmail(email)) {
        this.showError(this.dataset.emailError, true);
        this.email.focus();
        return;
      }
      const variant = this.chosen;
      if (!variant) {
        this.showError(this.dataset.error, false);
        return;
      }
      this.clearError();
      this.setSending(true);
      const abort = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = abort ? setTimeout(() => abort.abort(), TIMEOUT_MS) : 0;
      try {
        const response = await fetch(requestUrl(this.companyId), {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          headers: { 'content-type': 'application/vnd.api+json', revision: REVISION },
          body: JSON.stringify(buildPayload(email, variant.id)),
          signal: abort ? abort.signal : undefined
        });
        if (response.ok) {
          this.showSuccess(variant);
          return;
        }
        let body = null;
        try { body = await response.json(); } catch (error) { body = null; }
        const kind = classifyFailure(response.status, body);
        const code = body && Array.isArray(body.errors) && body.errors[0] ? body.errors[0].code : '';
        console.warn('[From the archive] Klaviyo did not accept the request', response.status, code || '');
        this.showError(kind === 'email' ? this.dataset.emailError : this.dataset.error, kind === 'email');
        if (kind === 'email') this.email.focus();
      } catch (error) {
        console.warn('[From the archive] Request failed', (error && error.name) || error);
        this.showError(this.dataset.error, false);
      } finally {
        if (timer) clearTimeout(timer);
        this.setSending(false);
      }
    }
  });
})();
